/* eslint-disable react/jsx-no-undef */
import React, { useMemo } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import { Input, Select } from "../../../../../../../../../components/Form";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { ShipmentLoadType, ShipmentType } from "../../../../../../../../../util/enums";
import { useShipmentContext } from "../../../../hooks/useContext";
import { DefaultProps } from "../../../../types";
import { Text } from "./styles";
import { SectionTitle } from "../../SectionTitle";
import { ViewModeComponent } from "./view-mode";

interface AirFormGeneralProps extends DefaultProps {
  referenceCodeGenerateAuto?: string;
}

export function AirFormGeneral({ onViewMode = false, errors = [] }: AirFormGeneralProps) {
  const { t: translate } = useTranslation();

  const { shipment, setShipment } = useShipmentContext();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const loadTypeOptions = useMemo(() => {
    switch (shipment?.shipmentType) {
      case ShipmentType.AIR:
        return [
          { label: ShipmentLoadType.CASE, value: ShipmentLoadType.CASE },
          { label: ShipmentLoadType.PALLET, value: ShipmentLoadType.PALLET },
          { label: ShipmentLoadType.OTHER, value: ShipmentLoadType.OTHER },
        ];
      default:
        return [{ label: ShipmentLoadType.OTHER, value: ShipmentLoadType.OTHER }];
    }
  }, [shipment?.shipmentType]);

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <SectionTitle title={translate("general")} />
      </Grid>
      <Grid container item xs={12} direction="row" spacing={3}>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: translate("mode"),
              value: shipment?.shipmentType ? translate(shipment.shipmentType.toLowerCase()) : "",
            }}
          >
            <Select
              name="type"
              required
              disabled={!!shipment?.id}
              label={translate("mode")}
              value={shipment?.shipmentType}
              onChange={(event: SelectChangeEvent<any>) =>
                setShipment({
                  ...(shipment || {}),
                  shipmentType: event.target.value,
                  commodity: "",
                  voyageNumber: "",
                  vesselName: "",
                  airwayBill: "",
                  fetchedData: false,
                  shipmentTracking: {
                    ...shipment?.shipmentTracking,
                    originPort: "",
                    finalPort: "",
                  },
                })
              }
              placeholder={translate("select")}
              error={fieldHasError("mode")}
              errorMessage={getErrorByField("mode")}
            >
              <MenuItem value={ShipmentType.SEA}>{translate("sea")}</MenuItem>

              <MenuItem value={ShipmentType.AIR}>{translate("air")}</MenuItem>
            </Select>
          </ViewModeComponent>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: `${translate("reference")}`,
              value: shipment?.containerId,
            }}
          >
            <Input
              label={`${translate("reference")}`}
              name="containerId"
              disabled={!!shipment?.id}
              required
              value={shipment?.containerId || ""}
              onChange={(event) => setShipment({ ...shipment, containerId: event.target.value })}
              error={fieldHasError("containerId")}
              errorMessage={getErrorByField("containerId")}
            />
          </ViewModeComponent>
        </Grid>

        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: translate("loadType"),
              value: shipment?.loadType,
            }}
          >
            <Select
              disabled={!!shipment?.id}
              name="loadType"
              label={translate("loadType")}
              placeholder={translate("selectALoadType")}
              value={shipment?.loadType}
              onChange={(event: SelectChangeEvent<any>) =>
                setShipment({ ...shipment, loadType: event.target.value })
              }
            >
              {loadTypeOptions?.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </ViewModeComponent>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: `${translate("nickname")}`,
              value: shipment?.nickName,
            }}
          >
            <Input
              data-cy="nickname"
              label={translate("nickname")}
              name="nickname"
              placeholder={translate("enterNickname")}
              value={shipment?.nickName ?? ""}
              onChange={(event) => {
                setShipment({ ...shipment, nickName: event.target.value });
              }}
            />
          </ViewModeComponent>
          <Grid item xs={12} style={{ paddingTop: 4 }}>
            <Text style={{ color: colors.gray500 }}>
              {translate("nicknameIsForYourOwnReference")}
            </Text>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
